import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

interface Product {
  id: string;
  name: string;
  product_code: string;
  barcode: string;
  brand: string;
  model: string;
  category_id: string | null;
  category_name?: string;
  sale_price: number;
  purchase_price: number;
  stock: number;
  shelf_location: string;
}

interface Category {
  id: string;
  name: string;
}

const emptyForm = {
  name: "", product_code: "", barcode: "", brand: "", model: "",
  category_id: "", sale_price: "", purchase_price: "", stock: "", shelf_location: "",
};

// ── EAN-13 barcode value generator ──────────────────────────────────────────
// Generates a valid EAN-13 from product barcode or product_code
function toEAN13(value: string): string {
  // Strip non-digits
  let digits = value.replace(/\D/g, "");
  // Pad or trim to 12 digits
  if (digits.length > 12) digits = digits.slice(-12);
  while (digits.length < 12) digits = "0" + digits;
  // Calculate check digit
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits[i]) * (i % 2 === 0 ? 1 : 3);
  }
  const check = (10 - (sum % 10)) % 10;
  return digits + check;
}

// Generate EAN-13 barcode SVG using simple bars (no library needed)
function generateEAN13SVG(ean: string, width = 220, height = 80): string {
  // EAN-13 encoding
  const L = ["0001101","0011001","0010011","0111101","0100011","0110001","1011100","1001110","1000110","1110100"];
  const G = ["0100111","0110011","0011011","0100001","0011101","0111001","0000101","0010001","0001001","0010111"];
  const R = ["1110010","1100110","1101100","1000010","1011100","1001110","1010000","1000100","1001000","1110100"];
  const PARITIES = ["LLLLLL","LLGLGG","LLGGLG","LLGGGL","LGLLGG","LGGLLG","LGGGLL","LGLGLG","LGLGGL","LGGLGL"];

  if (ean.length !== 13) return "";
  const first = parseInt(ean[0]);
  const parity = PARITIES[first];
  let bits = "101"; // start guard
  for (let i = 1; i <= 6; i++) {
    const d = parseInt(ean[i]);
    bits += parity[i-1] === "L" ? L[d] : G[d];
  }
  bits += "01010"; // middle guard
  for (let i = 7; i <= 12; i++) {
    bits += R[parseInt(ean[i])];
  }
  bits += "101"; // end guard

  const totalBars = 95;
  const barW = (width - 4) / totalBars; // 2px padding each side
  const textH = 10;
  const barcodeH = height - textH - 2;
  const startX = 2;

  let bars = "";
  for (let i = 0; i < bits.length; i++) {
    if (bits[i] === "1") {
      const x = startX + i * barW;
      // Guard bars are slightly taller
      const isGuard = i < 3 || (i >= 45 && i <= 49) || i >= 92;
      const bh = isGuard ? barcodeH + 2 : barcodeH;
      bars += `<rect x="${x.toFixed(2)}" y="0" width="${barW.toFixed(2)}" height="${bh}"/>`;
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="${width}" height="${height}" fill="white"/>
  <g fill="black">${bars}</g>
  <text x="${(width/2).toFixed(2)}" y="${height - 1}" text-anchor="middle" font-family="monospace" font-size="7" fill="black">${ean}</text>
</svg>`;
}

// Print barcodes for given products — full-page label design
async function printBarcodesWithModels(products: Product[], dealerId: string) {
  const BW = 400, BH = 130;

  // Fetch compatible models for this dealer
  let allModels: { barcode: string; group_name: string; model_name: string }[] = [];
  try {
    const { getSupabase } = await import("@/lib/supabase");
    const supabase = getSupabase();
    const { data } = await supabase
      .from("warehouse_compat_models")
      .select("barcode, group_name, model_name")
      .eq("dealer_id", dealerId);
    allModels = (data as any[]) || [];
  } catch { /* no models */ }

  const pages = products.map((p) => {
    const rawBarcode = p.barcode || p.product_code || p.id.replace(/-/g, "").slice(0, 12);
    const ean = toEAN13(rawBarcode);
    const svg = generateEAN13SVG(ean, BW, BH);
    const svgB64 = btoa(unescape(encodeURIComponent(svg)));
    const name = p.name.length > 45 ? p.name.slice(0, 45) + "…" : p.name;
    const cat  = p.category_name || "";
    const ref  = p.barcode || p.product_code || "";

    // Find compatible models for this product's barcode
    const compatModels = allModels
      .filter((m) => m.barcode.trim().toLowerCase() === rawBarcode.trim().toLowerCase())
      .map((m) => m.model_name || m.group_name)
      .filter(Boolean);

    const modelListHtml = compatModels.length > 0
      ? compatModels.map((m) => `<div class="model-item">${m}</div>`).join("")
      : `<div class="model-empty">—</div>`;

    return `
<div class="page">
  <div class="label">
    <div class="left-col">
      <div class="product-name">${name}</div>
      ${cat ? `<div class="field-row"><span class="field-label">Kategori:</span><span class="field-val">${cat}</span></div>` : ""}
      ${ref ? `<div class="field-row"><span class="field-label">Ref:</span><span class="field-val ref">${ref}</span></div>` : ""}
      <div class="barcode-wrap">
        <img src="data:image/svg+xml;base64,${svgB64}" alt="${ean}"/>
        <div class="ean-num">${ean}</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="right-col">
      <div class="models-title">Uyumlu Modeller</div>
      <div class="models-list">${modelListHtml}</div>
    </div>
  </div>
</div>`;
  }).join("");

  const win = window.open("", "_blank");
  if (!win) { alert("Popup engelleyici açık olabilir."); return; }
  win.document.write(`<!DOCTYPE html><html><head>
<meta charset="utf-8"/>
<title>Barkod Etiketleri</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }

  @page { size: 60mm 80mm; margin: 0; }

  html, body { width: 60mm; height: 80mm; background: white; overflow: hidden; }

  .page {
    width: 60mm;
    height: 80mm;
    page-break-after: always;
    page-break-inside: avoid;
    overflow: hidden;
    position: relative;
  }

  .label {
    position: absolute;
    top: 0; left: 0;
    width: 80mm;
    height: 60mm;
    transform-origin: 0 0;
    transform: rotate(90deg) translateY(-60mm);
    background: #fff;
    display: flex;
    flex-direction: row;
    padding: 2.5mm 3mm;
    gap: 2.5mm;
  }

  /* ── Left column ── */
  .left-col {
    width: 42mm;
    display: flex;
    flex-direction: column;
    gap: 1mm;
    flex-shrink: 0;
  }

  .product-name {
    font-family: Arial, sans-serif;
    font-size: 8pt;
    font-weight: 900;
    color: #000;
    line-height: 1.2;
    text-transform: uppercase;
    word-break: break-word;
  }

  .field-row {
    display: flex;
    align-items: baseline;
    gap: 1mm;
  }

  .field-label {
    font-family: Arial, sans-serif;
    font-size: 5pt;
    color: #888;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  .field-val {
    font-family: Arial, sans-serif;
    font-size: 5.5pt;
    color: #333;
    font-weight: 600;
  }

  .field-val.ref {
    font-family: 'Courier New', monospace;
    font-size: 5pt;
  }

  .barcode-wrap {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .barcode-wrap img {
    width: 40mm;
    height: auto;
  }

  .ean-num {
    font-family: 'Courier New', monospace;
    font-size: 4.5pt;
    color: #444;
    margin-top: 0.5mm;
  }

  /* ── Divider ── */
  .divider {
    width: 0.3mm;
    background: #cc3333;
    flex-shrink: 0;
    align-self: stretch;
  }

  /* ── Right column ── */
  .right-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .models-title {
    font-family: Arial, sans-serif;
    font-size: 6pt;
    font-weight: 900;
    color: #000;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 1.5mm;
    border-bottom: 0.3mm solid #ddd;
    padding-bottom: 0.5mm;
  }

  .models-list {
    display: flex;
    flex-direction: column;
    gap: 0.8mm;
    overflow: hidden;
  }

  .model-item {
    font-family: Arial, sans-serif;
    font-size: 5.5pt;
    color: #222;
    line-height: 1.3;
    word-break: break-word;
    padding-left: 1mm;
    border-left: 0.8mm solid #cc3333;
  }

  .model-empty {
    font-family: Arial, sans-serif;
    font-size: 5.5pt;
    color: #aaa;
  }
</style>
</head><body>
${pages}
<script>window.onload = () => { window.print(); };<\/script>
</body></html>`);
  win.document.close();
}

// Wrapper for sync contexts (toplu barkod yazdır butonu)
function printBarcodes(products: Product[], dealerId = "") {
  printBarcodesWithModels(products, dealerId);
}


export default function WarehouseStockPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId || user?.id || "";

  const [products, setProducts]   = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading]     = useState(true);
  const [query, setQuery]         = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterLow, setFilterLow] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteId, setDeleteId]   = useState<string | null>(null);
  const [deleting, setDeleting]   = useState(false);

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    const supabase = getSupabase();
    const [prodRes, catRes] = await Promise.all([
      supabase.from("products")
        .select("id, name, product_code, barcode, brand, model, category_id, sale_price, purchase_price, stock, shelf_location")
        .eq("dealer_id", dealerId)
        .order("name"),
      supabase.from("categories").select("id, name").eq("dealer_id", dealerId).order("name"),
    ]);

    const cats: Category[] = (catRes.data as Category[]) || [];
    setCategories(cats);

    const catMap: Record<string, string> = {};
    for (const c of cats) catMap[c.id] = c.name;

    const prods: Product[] = ((prodRes.data as Product[]) || []).map((p) => ({
      ...p,
      category_name: p.category_id ? (catMap[p.category_id] || "") : "",
    }));
    setProducts(prods);
    setLoading(false);
  }, [dealerId]);

  useEffect(() => { load(); }, [load]);

  // ── Excel Stok Güncelleme ───────────────────────────────────────────────────
  const [excelUpdating, setExcelUpdating] = useState(false);
  const [excelProgress, setExcelProgress] = useState(0);
  const [excelResult, setExcelResult] = useState<{ added: number; errors: number } | null>(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const excelFileRef = { current: null as HTMLInputElement | null };

  // ── Excel şifre koruması ─────────────────────────────────────────────────────
  const EXCEL_PASSWORD = "Enc256Enc";
  const [excelPwModal, setExcelPwModal]   = useState(false);
  const [excelPwInput, setExcelPwInput]   = useState("");
  const [excelPwError, setExcelPwError]   = useState("");
  const excelTriggerRef = { current: null as HTMLInputElement | null };

  const openExcelPwModal = () => {
    setExcelPwInput("");
    setExcelPwError("");
    setExcelPwModal(true);
  };

  const confirmExcelPw = () => {
    if (excelPwInput !== EXCEL_PASSWORD) {
      setExcelPwError("Geçersiz şifre!");
      return;
    }
    setExcelPwModal(false);
    excelTriggerRef.current?.click();
  };

  // ── Toplu Ürün Girişi (stok ekleme) ─────────────────────────────────────────
  const [bulkStockModal, setBulkStockModal]   = useState(false);
  const [bulkEntries, setBulkEntries]         = useState<{ id: string; name: string; addQty: string }[]>([]);
  const [bulkSaving, setBulkSaving]           = useState(false);
  const [bulkQuery, setBulkQuery]             = useState("");

  const openBulkStock = () => {
    setBulkQuery("");
    setBulkEntries(
      products.map((p) => ({ id: p.id, name: p.name, addQty: "" }))
    );
    setBulkStockModal(true);
  };

  const saveBulkStock = async () => {
    const toUpdate = bulkEntries.filter((e) => e.addQty.trim() !== "" && parseInt(e.addQty) !== 0);
    if (!toUpdate.length) { setBulkStockModal(false); return; }
    setBulkSaving(true);
    const supabase = getSupabase();
    for (const entry of toUpdate) {
      const prod = products.find((p) => p.id === entry.id);
      if (!prod) continue;
      const newStock = prod.stock + parseInt(entry.addQty);
      await supabase.from("products").update({ stock: Math.max(0, newStock) }).eq("id", entry.id);
    }
    setBulkSaving(false);
    setBulkStockModal(false);
    load();
  };

  const handleExcelUpdate = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !dealerId) return;
    setExcelUpdating(true);
    setExcelProgress(0);
    setExcelResult(null);
    try {
      const XLSX = await import("xlsx");
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][];

      // Detect format (reuse same logic as stock-import page)
      let headerIdx = -1;
      let format: "akinsoft" | "servis" | "unknown" = "unknown";
      for (let i = 0; i < Math.min(raw.length, 10); i++) {
        const row = raw[i];
        if (!row) continue;
        const cells = (row as unknown[]).map((c) => String(c ?? "").trim());
        const joined = cells.join("|");
        if (joined.includes("Stok Kodu") && joined.includes("Stok Adı"))       { headerIdx = i; format = "akinsoft"; break; }
        if (joined.includes("Malzeme Kodu") && joined.includes("Malzeme Tanımı")) { headerIdx = i; format = "servis";   break; }
      }
      if (headerIdx === -1) headerIdx = 0;

      const headers = (raw[headerIdx] ?? []).map((c) => String(c ?? "").trim());
      const col = (name: string) => headers.findIndex((h) => h === name || h.toLowerCase().includes(name.toLowerCase()));
      const idxCode  = format === "akinsoft" ? col("Stok Kodu")    : col("Malzeme Kodu");
      const idxName  = format === "akinsoft" ? col("Stok Adı")     : col("Malzeme Tanımı");
      const idxStock = format === "akinsoft" ? col("Kalan Miktar") : col("Servis Mevcut Stok");
      const idxPrice = format === "akinsoft" ? col("Satış Fiyatı 1") : -1;
      const idxCat   = format === "akinsoft" ? col("Grubu") : -1;
      const idxBarc  = format === "akinsoft" ? col("Barkodu") : -1;

      const toNum = (v: unknown) => { const n = parseFloat(String(v ?? "").replace(",",".")); return isNaN(n) ? 0 : n; };
      const toStr = (v: unknown) => String(v ?? "").trim();

      const rows: { name: string; code: string; barcode: string; category: string; stock: number; price: number }[] = [];
      for (let i = headerIdx + 1; i < raw.length; i++) {
        const row = raw[i] as unknown[];
        if (!row) continue;
        const name = idxName >= 0 ? toStr(row[idxName]) : "";
        const code = idxCode >= 0 ? toStr(row[idxCode]) : "";
        if (!name && !code) continue;
        rows.push({
          name: name || code,
          code,
          barcode: idxBarc >= 0 ? toStr(row[idxBarc]) : "",
          category: idxCat >= 0 ? toStr(row[idxCat]) : "",
          stock: idxStock >= 0 ? toNum(row[idxStock]) : 0,
          price: idxPrice >= 0 ? toNum(row[idxPrice]) : 0,
        });
      }

      if (rows.length === 0) throw new Error("Excel'de işlenebilir kayıt bulunamadı.");

      const supabase = getSupabase();

      // 1. Delete all existing products for this dealer (NOT sales data)
      const { error: delErr } = await supabase.from("products").delete().eq("dealer_id", dealerId);
      if (delErr) throw delErr;

      // 2. Ensure categories exist
      const catNames = [...new Set(rows.map((r) => r.category).filter(Boolean))];
      const catMap: Record<string, string> = {};
      const { data: existingCats } = await supabase.from("categories").select("id, name").eq("dealer_id", dealerId);
      for (const c of existingCats ?? []) catMap[c.name] = c.id;
      for (const catName of catNames) {
        if (!catMap[catName]) {
          const { data: nc } = await supabase.from("categories").insert({ dealer_id: dealerId, name: catName }).select("id, name").single();
          if (nc) catMap[(nc as any).name] = (nc as any).id;
        }
      }

      // 3. Insert new products in batches
      const BATCH = 50;
      let added = 0; let errors = 0;
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH).map((r) => ({
          dealer_id:     dealerId,
          name:          r.name,
          product_code:  r.code || null,
          barcode:       r.barcode || null,
          category_id:   catMap[r.category] || null,
          sale_price:    r.price,
          purchase_price: 0,
          stock:         r.stock,
        }));
        const { error: ie } = await supabase.from("products").insert(batch);
        if (ie) errors += batch.length;
        else added += batch.length;
        setExcelProgress(Math.round(((i + BATCH) / rows.length) * 100));
        await new Promise((r) => setTimeout(r, 10));
      }

      setExcelResult({ added, errors });
      load();
      logAction(dealerId, user?.id || null, LOG_ACTIONS.IMPORT, `Excel Stok Güncelleme: ${added} ürün yüklendi, ${errors} hata`);
    } catch (err: any) {
      setExcelResult({ added: 0, errors: -1 });
    } finally {
      setExcelUpdating(false);
      if (e.target) e.target.value = "";
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = products.filter((p) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || [p.name, p.product_code, p.barcode, p.brand, p.model]
      .some((v) => v?.toLowerCase().includes(q));
    const matchCat = !filterCat || p.category_id === filterCat;
    const matchLow = !filterLow || p.stock <= 3;
    return matchQ && matchCat && matchLow;
  });

  // ── Open modal ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowModal(true);
  };

  const openEdit = (p: Product) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      product_code: p.product_code || "",
      barcode: p.barcode || "",
      brand: p.brand || "",
      model: p.model || "",
      category_id: p.category_id || "",
      sale_price: p.sale_price != null ? String(p.sale_price) : "",
      purchase_price: p.purchase_price != null ? String(p.purchase_price) : "",
      stock: p.stock != null ? String(p.stock) : "",
      shelf_location: p.shelf_location || "",
    });
    setFormError("");
    setShowModal(true);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const save = async () => {
    if (!form.name.trim()) { setFormError("Ürün adı zorunludur!"); return; }
    setSaving(true);
    setFormError("");
    const supabase = getSupabase();
    const payload = {
      dealer_id:      dealerId,
      name:           form.name.trim(),
      product_code:   form.product_code.trim() || null,
      barcode:        form.barcode.trim() || null,
      brand:          form.brand.trim() || null,
      model:          form.model.trim() || null,
      category_id:    form.category_id || null,
      sale_price:     parseFloat(form.sale_price) || 0,
      purchase_price: parseFloat(form.purchase_price) || 0,
      stock:          parseInt(form.stock) || 0,
      shelf_location: form.shelf_location.trim() || null,
    };

    try {
      if (editingId) {
        const { error } = await supabase.from("products").update(payload).eq("id", editingId);
        if (error) throw error;
        logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_UPDATE, `Depo stok güncellendi: ${form.name}`);
      } else {
        const { error } = await supabase.from("products").insert(payload);
        if (error) throw error;
        logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_ADD, `Depo stok eklendi: ${form.name}`);
      }
      setShowModal(false);
      load();
    } catch (e: any) {
      setFormError(e.message || "Kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const deleteProduct = async () => {
    if (!deleteId) return;
    setDeleting(true);
    const supabase = getSupabase();
    const prod = products.find((p) => p.id === deleteId);
    await supabase.from("products").delete().eq("id", deleteId);
    logAction(dealerId, user?.id || null, LOG_ACTIONS.PRODUCT_DELETE, `Depo stok silindi: ${prod?.name}`);
    setDeleteId(null);
    setDeleting(false);
    load();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">Depo Stok Yönetimi</h4>
          <p className="text-sm text-foreground-500 mt-0.5">{products.length} ürün · Filtrele ve yönet</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/warehouse/model-control"
            className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200/70 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium cursor-pointer transition-colors"
          >
            <i className="ri-list-settings-line"></i> Model Kontrol
          </a>
          <button
            onClick={openBulkStock}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer transition-colors"
            title="Toplu ürün girişi — birden fazla ürüne aynı anda stok ekle"
          >
            <i className="ri-archive-line"></i> Toplu Ürün Girişi
          </button>
          <button
            onClick={openExcelPwModal}
            disabled={excelUpdating}
            className={`flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer transition-colors ${excelUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
            title="Mevcut tüm stok silinir, Excel'deki verilerle yeniden yüklenir. Satış raporları etkilenmez."
          >
            <i className={`${excelUpdating ? "ri-loader-4-line animate-spin" : "ri-file-excel-2-line"}`}></i>
            {excelUpdating ? `Güncelleniyor %${excelProgress}` : "Excel'den Stok Güncelle"}
          </button>
          {/* Gizli file input - şifre onayından sonra tetiklenir */}
          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            ref={(el) => { excelTriggerRef.current = el; }}
            onChange={handleExcelUpdate}
          />
          <button
            onClick={() => printBarcodes(filtered, dealerId)}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-100 hover:bg-secondary-200/70 text-secondary-700 border border-secondary-200 rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-barcode-line"></i> Toplu Barkod Yazdır
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer transition-colors"
          >
            <i className="ri-add-line"></i> Ürün Ekle
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ürün adı, kodu, barkod ara..."
            className="w-full pl-9 pr-4 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button
          onClick={() => setFilterLow(!filterLow)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer border transition-colors ${
            filterLow
              ? "bg-amber-100 border-amber-300 text-amber-800"
              : "bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100"
          }`}
        >
          <i className="ri-alert-line"></i> Düşük Stok
        </button>
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <i className="ri-box-3-line text-3xl text-foreground-300 block mb-2"></i>
            <p className="text-sm text-foreground-400">Ürün bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
            <table className="w-full text-sm min-w-[750px]">
              <thead className="bg-background-100">
                <tr>
                  {["Ürün Adı", "Kod / Barkod", "Kategori", "Raf", "Satış Fiyatı", "Stok", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-foreground-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-background-200/70 hover:bg-background-100/40 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground-950">{p.name}</p>
                      {p.brand && <p className="text-xs text-foreground-400">{p.brand}{p.model ? ` · ${p.model}` : ""}</p>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground-500">
                      {p.product_code && <div>{p.product_code}</div>}
                      {p.barcode && <div className="text-foreground-400">{p.barcode}</div>}
                    </td>
                    <td className="px-4 py-3 text-foreground-500 text-xs">{p.category_name || "—"}</td>
                    <td className="px-4 py-3 text-foreground-500 text-xs">{p.shelf_location || "—"}</td>
                    <td className="px-4 py-3 font-semibold text-foreground-950">₺{Number(p.sale_price).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.stock <= 0 ? "bg-accent-100 text-accent-700" :
                        p.stock <= 3  ? "bg-amber-100 text-amber-700"  :
                        "bg-emerald-100 text-emerald-700"
                      }`}>
                        {p.stock <= 0 ? <i className="ri-close-circle-line"></i> :
                         p.stock <= 3  ? <i className="ri-alert-line"></i>         :
                         <i className="ri-check-line"></i>}
                        {p.stock} adet
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => printBarcodes([p], dealerId)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-secondary-100 hover:text-secondary-600 cursor-pointer transition-colors" title="Barkod Yazdır">
                          <i className="ri-barcode-line text-sm"></i>
                        </button>
                        <button onClick={() => openEdit(p)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-primary-100 hover:text-primary-600 cursor-pointer transition-colors" title="Düzenle">
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          title="Stok Girişi"
                          onClick={async () => {
                            const qty = window.prompt(`"${p.name}" için eklenecek adet:`);
                            if (!qty || isNaN(parseInt(qty))) return;
                            const supabase = getSupabase();
                            const newStock = p.stock + parseInt(qty);
                            await supabase.from("products").update({ stock: Math.max(0, newStock) }).eq("id", p.id);
                            load();
                          }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-emerald-100 hover:text-emerald-600 cursor-pointer transition-colors"
                        >
                          <i className="ri-add-box-line text-sm"></i>
                        </button>
                        <button onClick={() => setDeleteId(p.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-accent-100 hover:text-accent-600 cursor-pointer transition-colors">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-t-2xl lg:rounded-2xl p-6 w-full max-w-lg mx-0 lg:mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-lg font-semibold text-foreground-950">{editingId ? "Ürünü Düzenle" : "Yeni Ürün Ekle"}</h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Adı *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Kodu</label>
                  <input type="text" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Barkod</label>
                  <input type="text" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Marka</label>
                  <input type="text" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Model</label>
                  <input type="text" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Kategori</label>
                  <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer">
                    <option value="">Kategorisiz</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Raf No</label>
                  <input type="text" value={form.shelf_location} onChange={(e) => setForm({ ...form, shelf_location: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Satış Fiyatı</label>
                  <input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Alış Fiyatı</label>
                  <input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Stok Adedi</label>
                  <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="0" />
                </div>
              </div>
            </div>

            {formError && (
              <div className="mt-3 p-3 bg-accent-50 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i> {formError}
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium cursor-pointer hover:bg-background-200/70">İptal</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50 transition-colors">
                {saving ? <><i className="ri-loader-4-line animate-spin mr-1"></i>Kaydediliyor...</> : <><i className="ri-check-line mr-1"></i>Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteId(null)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-accent-600"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-950 mb-1">Ürünü Sil</h4>
            <p className="text-center text-sm text-foreground-500 mb-5">
              <strong>{products.find((p) => p.id === deleteId)?.name}</strong> ürünü silinecek. Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium cursor-pointer">İptal</button>
              <button onClick={deleteProduct} disabled={deleting} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {deleting ? "Siliniyor..." : "Evet, Sil"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Şifre Modalı */}
      {excelPwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setExcelPwModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h4 className="text-lg font-semibold text-foreground-950">Excel Stok Güncelle</h4>
                <p className="text-xs text-foreground-400 mt-0.5">Bu işlem mevcut tüm stoku siler ve yeniden yükler</p>
              </div>
              <button onClick={() => setExcelPwModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700 flex items-start gap-2">
              <i className="ri-alert-line shrink-0 mt-0.5"></i>
              <span>Mevcut tüm ürünler silinip Excel'deki verilerle değiştirilecek. Satış raporları etkilenmez.</span>
            </div>
            <label className="block text-xs font-medium text-foreground-500 mb-1">Şifre</label>
            <input
              type="password"
              value={excelPwInput}
              onChange={(e) => { setExcelPwInput(e.target.value); setExcelPwError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") confirmExcelPw(); }}
              autoFocus
              placeholder="••••••••"
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 mb-2"
            />
            {excelPwError && <p className="text-xs text-accent-600 mb-3 flex items-center gap-1"><i className="ri-error-warning-line"></i>{excelPwError}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setExcelPwModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer hover:bg-background-200/70">İptal</button>
              <button onClick={confirmExcelPw} className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer">
                <i className="ri-file-excel-2-line mr-1"></i>Devam Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toplu Ürün Girişi Modalı */}
      {bulkStockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setBulkStockModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h4 className="text-lg font-semibold text-foreground-950">Toplu Ürün Girişi</h4>
              <button onClick={() => setBulkStockModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-3 shrink-0">Eklenecek adet girin (boş bırakılan ürünler güncellenmez)</p>
            <div className="relative mb-3 shrink-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
              <input
                type="text"
                value={bulkQuery}
                onChange={(e) => setBulkQuery(e.target.value)}
                placeholder="Ürün ara..."
                className="w-full pl-8 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-background-200/70 border border-background-200/70 rounded-xl">
              {bulkEntries
                .filter((e) => !bulkQuery || e.name.toLowerCase().includes(bulkQuery.toLowerCase()))
                .map((entry) => {
                  const prod = products.find((p) => p.id === entry.id);
                  return (
                    <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-950 truncate">{entry.name}</p>
                        <p className="text-xs text-foreground-400">Mevcut stok: <span className="font-semibold text-foreground-700">{prod?.stock ?? 0}</span></p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-foreground-400">+ Adet:</span>
                        <input
                          type="number"
                          value={entry.addQty}
                          onChange={(e) => setBulkEntries((prev) => prev.map((en) => en.id === entry.id ? { ...en, addQty: e.target.value } : en))}
                          placeholder="0"
                          className="w-20 px-2 py-1 bg-background-100 border border-background-200/70 rounded-lg text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary-400"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="flex gap-2 mt-4 shrink-0">
              <button onClick={() => setBulkStockModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer hover:bg-background-200/70">İptal</button>
              <button onClick={saveBulkStock} disabled={bulkSaving} className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {bulkSaving ? <><i className="ri-loader-4-line animate-spin mr-1"></i>Kaydediliyor...</> : <><i className="ri-check-line mr-1"></i>Stokları Güncelle</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Güncelleme Sonuç Modalı */}
      {excelResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setExcelResult(null)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${excelResult.errors === -1 ? "bg-accent-100" : "bg-emerald-100"}`}>
              <i className={`text-2xl ${excelResult.errors === -1 ? "ri-error-warning-line text-accent-600" : "ri-checkbox-circle-fill text-emerald-500"}`}></i>
            </div>
            {excelResult.errors === -1 ? (
              <p className="text-center font-semibold text-accent-700 mb-4">Dosya okunamadı. Formatı kontrol edin.</p>
            ) : (
              <>
                <p className="text-center text-lg font-bold text-foreground-950 mb-1">{excelResult.added} ürün yüklendi</p>
                {excelResult.errors > 0 && <p className="text-center text-sm text-amber-600 mb-1">{excelResult.errors} kayıt atlandı</p>}
                <p className="text-center text-xs text-foreground-400 mb-4">Satış raporları ve geçmiş satışlar korundu</p>
              </>
            )}
            <button onClick={() => setExcelResult(null)} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer">Tamam</button>
          </div>
        </div>
      )}
    </div>
  );
}
